// Deterministic merge resolution. Spec: docs/mechanics.md §4.
import { centroid, coordKey, compareCoord, distance, neighbors } from './hex.js';
import { addTile, getTile, listTiles, removeTile, replaceTile } from './board.js';
// Returns true if a tile's state permits merging with same-color neighbors.
// W18 Embers: reactive tiles ARE merge-eligible — they're a live tile color
// like normal/charged, just with a side-effect on merge participation.
// The detonation is dispatched in resolveMerges after the group's removal.
function canMerge(t) {
    return t.state.kind === 'normal' || t.state.kind === 'charged' || t.state.kind === 'reactive';
}
// Locked tiles unlock (transition to 'normal') whenever a charged tile whose
// color matches the locked tile's `key` is adjacent. This fires as a pass
// over the whole board at the START of each merge-resolution iteration, so
// a charged tile that gets placed by an earlier merge in the same slide can
// unlock an adjacent locked tile before findGroups runs on the next pass —
// letting a newly-unlocked tile participate in chains within the same slide.
// Runs in O(locked-tiles * 6). Idempotent (already-unlocked tiles no-op).
function unlockAdjacentLocks(board) {
    let next = board;
    // Snapshot the locked tiles first — replaceTile returns a new board on
    // each call, so iterating over next.tiles while mutating would skip or
    // double-process entries in rare orderings.
    const lockedTiles = [];
    for (const t of next.tiles.values()) {
        if (t.state.kind === 'locked') {
            lockedTiles.push({ id: t.id, coord: t.coord, key: t.state.key });
        }
    }
    for (const { id, coord, key } of lockedTiles) {
        const hasMatchingChargedNeighbor = neighbors(coord).some((nb) => {
            const nt = getTile(next, nb);
            return nt !== null && nt.state.kind === 'charged' && nt.color === key;
        });
        if (hasMatchingChargedNeighbor) {
            // Unlock: reset state to a plain normal tile. The tile's own color
            // and id are preserved — only the state transitions. Next findGroups
            // pass will treat it as a merge-eligible normal tile.
            next = replaceTile(next, id, { state: { kind: 'normal' } });
        }
    }
    return next;
}
// Find all same-color mergeable connected components of size >= 3.
function findGroups(board) {
    const tiles = listTiles(board);
    const eligible = tiles.filter(canMerge);
    const byKey = new Map(eligible.map((t) => [coordKey(t.coord), t]));
    const seen = new Set();
    const groups = [];
    for (const start of eligible) {
        if (seen.has(start.id))
            continue;
        const group = [];
        const stack = [start];
        while (stack.length) {
            const cur = stack.pop();
            if (seen.has(cur.id))
                continue;
            seen.add(cur.id);
            group.push(cur);
            for (const nb of neighbors(cur.coord)) {
                const nT = byKey.get(coordKey(nb));
                if (nT && nT.color === cur.color && !seen.has(nT.id))
                    stack.push(nT);
            }
        }
        if (group.length >= 3)
            groups.push(group);
    }
    return groups;
}
// Pick the group to collapse first. Tie-breaks per mechanics.md §4 step 3.
function pickGroup(groups, lastMovedId) {
    if (groups.length === 1)
        return groups[0];
    if (lastMovedId !== null) {
        const withLast = groups.find((g) => g.some((t) => t.id === lastMovedId));
        if (withLast)
            return withLast;
    }
    // Largest first; then lex-min of group's min cell.
    return [...groups].sort((a, b) => {
        if (a.length !== b.length)
            return b.length - a.length;
        const minA = [...a].sort((x, y) => compareCoord(x.coord, y.coord))[0];
        const minB = [...b].sort((x, y) => compareCoord(x.coord, y.coord))[0];
        return compareCoord(minA.coord, minB.coord);
    })[0];
}
// Pick result cell for the charged tile replacing the group. Spec §4 step 4.
function resultCoord(group, lastMovedId) {
    const lastMoved = lastMovedId !== null ? group.find((t) => t.id === lastMovedId) : undefined;
    const groupCoords = group.map((t) => t.coord);
    const tiebreak = lastMoved?.coord;
    const c = centroid(groupCoords, tiebreak);
    // If centroid is a group cell, use it. Otherwise pick nearest group cell; tie-break to last-moved.
    if (groupCoords.some((g) => g.q === c.q && g.r === c.r))
        return c;
    let best = groupCoords[0];
    let bestDist = distance(best, c);
    for (const g of groupCoords) {
        const d = distance(g, c);
        if (d < bestDist || (d === bestDist && lastMoved && g.q === lastMoved.coord.q && g.r === lastMoved.coord.r)) {
            best = g;
            bestDist = d;
        }
    }
    return best;
}
export function resolveMerges(board, lastMovedId) {
    let cur = board;
    const events = [];
    let guard = 0;
    const initialTileCount = cur.tiles.size;
    while (true) {
        if (++guard > initialTileCount + 5)
            throw new Error('merge resolution failed to terminate');
        // W11 "Keep" mechanic: unlock any locked tile that now has a matching-
        // color charged tile adjacent. Idempotent — no-op unless the previous
        // iteration's placed charged tile happens to border a locked one with
        // a matching key. See unlockAdjacentLocks for detail.
        cur = unlockAdjacentLocks(cur);
        const groups = findGroups(cur);
        if (groups.length === 0)
            break;
        const group = pickGroup(groups, lastMovedId);
        const color = group[0].color;
        const inputChainDepth = group.reduce((m, t) => {
            if (t.state.kind === 'charged')
                return Math.max(m, t.state.chainDepth);
            return m;
        }, 0);
        const chainDepth = inputChainDepth + 1;
        const resCoord = resultCoord(group, lastMovedId);
        // Remove all group tiles.
        let next = cur;
        for (const t of group)
            next = removeTile(next, t.id);
        // Spec §4 step 5: chains (chainDepth >= 2, i.e., group contained a charged tile) annihilate
        // entirely — no new tile is placed. Fresh merges (chainDepth === 1) place a charged tile.
        const annihilates = chainDepth >= 2;
        if (!annihilates) {
            if (getTile(next, resCoord))
                throw new Error('result cell unexpectedly occupied after group removal');
            next = addTile(next, resCoord, color, { kind: 'charged', chainDepth });
        }
        events.push({ cells: group.map((t) => t.coord), color, resultCoord: resCoord, chainDepth });
        // W18 Embers detonation: if any tile in the just-removed merge group
        // was reactive, clear its non-charged-non-reactive neighbors. Multiple
        // reactives in the same group each detonate independently — a 6-tile
        // reactive merge could clear up to 6×6=36 neighbor cells (capped by
        // board geometry and dedupe). The newly-placed charged tile from this
        // merge is exempt because (a) it's charged, and (b) it didn't exist
        // when the reactive's neighbor list was computed (its coord may be a
        // neighbor of one of the original reactive cells — see exemption note
        // below).
        //
        // Order matters: we compute the neighbor set BEFORE adding the
        // charged tile. The reactive's "neighbor at time of merge" is a fixed
        // physical region; the charged-tile-just-placed sits inside the
        // merge's footprint, not the neighbor set. So the charged is safely
        // outside the detonation radius regardless of where it lands within
        // the group's centroid.
        const reactiveTiles = group.filter((t) => t.state.kind === 'reactive');
        if (reactiveTiles.length > 0) {
            const neighborKeys = new Set();
            const groupKeys = new Set(group.map((t) => coordKey(t.coord)));
            for (const r of reactiveTiles) {
                for (const nb of neighbors(r.coord)) {
                    const nk = coordKey(nb);
                    if (!groupKeys.has(nk))
                        neighborKeys.add(nk);
                }
            }
            for (const nk of neighborKeys) {
                const tid = next.tileAt.get(nk);
                if (tid === undefined)
                    continue;
                const t = next.tiles.get(tid);
                if (!t)
                    continue;
                // Spare charged tiles (precious resource) and other reactives
                // (cascade-ambiguity exemption). All other states (normal,
                // locked, mixer, fragile) get cleared.
                if (t.state.kind === 'charged' || t.state.kind === 'reactive')
                    continue;
                next = removeTile(next, tid);
            }
        }
        // For chain-continuation tie-break, the new charged becomes the "last moved"; after annihilation
        // there's nothing left to chain from, so lastMovedId is cleared.
        lastMovedId = annihilates ? null : next.nextId - 1;
        cur = next;
    }
    return { board: cur, events };
}
